import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, MessageSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveFileUrl } from "@/components/FileDropzone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Avatar({ user, size = 40 }) {
  const url = resolveFileUrl(user?.avatar_url);
  return (
    <div
      style={{ width: size, height: size }}
      className="border border-black bg-[#F7931A]/20 flex items-center justify-center font-display font-black overflow-hidden shrink-0"
    >
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover" />
      ) : (
        <span>{user?.name?.[0]?.toUpperCase()}</span>
      )}
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [params, setParams] = useSearchParams();
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/conversations").then((r) => {
      setConvos(r.data);
      const target = params.get("to");
      if (target) {
        const found = r.data.find((c) => c.user.id === target);
        if (found) setActive(found);
      } else if (r.data.length > 0 && !active) {
        setActive(r.data[0]);
      }
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active.user.id}`).then((r) => setThread(r.data));
  }, [active]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/messages", {
        to_user_id: active.user.id,
        text,
      });
      setThread((t) => [...t, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-6 h-6 text-[#F7931A]" />
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Messages</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-black bg-white min-h-[70vh]">
        {/* Sidebar */}
        <aside className="border-r-2 border-black overflow-y-auto max-h-[70vh]" data-testid="convo-list">
          {convos.length === 0 && (
            <div className="p-6 font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
              No matches yet. Try <Link to="/bitdate" className="underline decoration-[#F7931A] decoration-2">BitDate</Link>.
            </div>
          )}
          {convos.map((c) => (
            <button
              key={c.user.id}
              onClick={() => {
                setActive(c);
                setParams({ to: c.user.id });
              }}
              data-testid={`convo-${c.user.id}`}
              className={`w-full text-left flex items-center gap-3 p-4 border-b border-black/10 hover:bg-[#F7931A]/10 transition-colors ${
                active?.user.id === c.user.id ? "bg-[#F7931A]/10" : ""
              }`}
            >
              <Avatar user={c.user} />
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm truncate">{c.user.name}</div>
                <div className="text-xs text-[#0A0A0A]/60 truncate">
                  {c.last_message?.text || "Say hi."}
                </div>
              </div>
            </button>
          ))}
        </aside>

        {/* Thread */}
        <section className="md:col-span-2 flex flex-col max-h-[70vh]">
          {!active ? (
            <div className="flex-1 flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
              Select a conversation
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b-2 border-black flex items-center gap-3">
                <Avatar user={active.user} />
                <div className="font-display font-bold">{active.user.name}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="messages-thread">
                {thread.length === 0 && (
                  <div className="text-center font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
                    Start the conversation
                  </div>
                )}
                {thread.map((m) => {
                  const mine = m.from_user_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      data-testid={`message-${m.id}`}
                      className={`flex ${mine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] px-4 py-2 border-2 border-black ${
                          mine ? "bg-[#F7931A]" : "bg-white"
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="p-4 border-t-2 border-black flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="message-input"
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 bg-[#FDFBF7] border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#F7931A]"
                />
                <button
                  onClick={send}
                  disabled={!text.trim()}
                  data-testid="message-send"
                  className="px-4 bg-[#F7931A] border-2 border-black disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
